import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Copy, Trash2, Save, FileText, Box, X, AlertTriangle, CheckCircle2 } from "lucide-react";
import { useVanDesign } from "./VanDesignContext";
import { vanDesignApi } from "@/api/vanDesign";
import { VanHeader, NCard, NBtn, MonoBadge } from "./vanUI";
import VanSchematic from "./VanSchematic";
import {
  MODULE_CATALOG, MODULE_TYPES, catalogOf, newModule, moduleCost, projectEstimate,
  validateLayout, tierOf, diffLabel,
} from "./vanData";

export default function VanDesignEditor({ onBack }) {
  const { project, setModules, setScreen, patch } = useVanDesign();
  const [selectedId, setSelectedId] = useState(null);
  const [showCatalog, setShowCatalog] = useState(false);
  const [saving, setSaving] = useState(false);

  const tierKey = project.goals.budget_tier;
  const tier = tierOf(tierKey);
  const modules = project.modules || [];
  const selected = modules.find((m) => m.id === selectedId);

  const v = useMemo(() => validateLayout(project.van, modules), [project.van, modules]);
  const estimate = projectEstimate(modules, tierKey);
  const overBudget = estimate > tier.projectRange[1];

  const addModule = (type) => {
    const m = newModule(type, project.van?.interior || { width: 1750, length: 3200 });
    if (!m) return;
    setModules((ms) => [...ms, m]); setSelectedId(m.id); setShowCatalog(false);
  };
  const move = (id, x, y) => setModules((ms) => ms.map((m) => (m.id === id ? { ...m, x, y } : m)));
  const removeM = (id) => { setModules((ms) => ms.filter((m) => m.id !== id)); if (selectedId === id) setSelectedId(null); };
  const dupM = (id) => {
    const m = modules.find((x) => x.id === id); if (!m) return;
    const copy = { ...m, id: Math.random().toString(36).slice(2, 10), x: Math.min(m.x + 60, (project.van?.interior?.width || 1750) - m.w) };
    setModules((ms) => [...ms, copy]); setSelectedId(copy.id);
  };
  const resize = (id, key, val) => setModules((ms) => ms.map((m) => (m.id === id ? { ...m, [key]: Math.max(200, +val || 0) } : m)));

  const save = async () => {
    setSaving(true);
    try {
      const name = project.name && project.name !== "Untitled Van Build"
        ? project.name : `${project.van?.model || "Van"} — ${tier.label}`;
      const saved = await vanDesignApi.saveProject({
        id: project.id, name, van: project.van, goals: project.goals, modules, ai: project.ai,
      });
      patch({ id: saved.id, name: saved.name });
      toast.success("Design saved");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const totalWarns = Object.values(v.perModule).reduce((s, a) => s + a.length, 0) + v.global.length;

  return (
    <div className="absolute inset-0 z-40 flex flex-col bg-[#FDFBF7] max-w-[412px] mx-auto" data-testid="van-editor">
      <VanHeader title="Layout Editor" sub="Tap a module · drag to move" onBack={onBack} step={`${modules.length} mods`} testid="van-editor-header" />
      <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar px-4 py-3 space-y-3">
        {/* status row */}
        <div className="flex items-center gap-2">
          {totalWarns === 0
            ? <MonoBadge tone="ok"><CheckCircle2 size={11} /> Layout OK</MonoBadge>
            : <MonoBadge tone="warn"><AlertTriangle size={11} /> {totalWarns} issue{totalWarns > 1 ? "s" : ""}</MonoBadge>}
          <MonoBadge>Walkway {v.corridor}mm</MonoBadge>
          <MonoBadge tone={overBudget ? "warn" : "gold"}>${estimate.toLocaleString()}</MonoBadge>
        </div>

        {v.global.length > 0 && (
          <NCard className="p-2.5 bg-[#FFE0E0]">
            {v.global.map((w, i) => <p key={i} className="font-mono text-[9px] text-[#DC2626] font-bold">⚠ {w}</p>)}
          </NCard>
        )}

        <VanSchematic van={project.van} modules={modules} editable selectedId={selectedId} onSelect={setSelectedId} onMove={move} maxW={372} maxH={430} />

        {/* selected module settings */}
        {selected ? (
          <NCard className="p-3" data-testid="van-module-panel">
            <div className="flex items-center justify-between mb-2">
              <p className="font-serif text-lg text-black">{catalogOf(selected.type)?.label}</p>
              <button onClick={() => setSelectedId(null)} className="w-7 h-7 grid place-items-center border-2 border-black rounded-lg bg-white"><X size={13} /></button>
            </div>
            <div className="grid grid-cols-2 gap-2 mb-2">
              {["w", "h"].map((k) => (
                <label key={k} className="block">
                  <span className="font-mono text-[8px] uppercase tracking-widest text-black/50">{k === "w" ? "Width (mm)" : "Length (mm)"}</span>
                  <input value={selected[k]} inputMode="numeric" onChange={(e) => resize(selected.id, k, e.target.value.replace(/\D/g, ""))}
                    data-testid={`van-module-${k}`} className="mt-0.5 w-full bg-white border-2 border-black rounded-lg px-2 py-1.5 font-mono text-xs" />
                </label>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-2 mb-2">
              <div className="border-2 border-black rounded-lg p-2 bg-[#FDFBF7]">
                <p className="font-mono text-[8px] uppercase text-black/50">Material</p>
                <p className="font-mono text-[10px] text-black font-bold leading-tight mt-0.5">{tier.board}</p>
              </div>
              <div className="border-2 border-black rounded-lg p-2 bg-[#FDFBF7]">
                <p className="font-mono text-[8px] uppercase text-black/50">Budget impact</p>
                <p className="font-mono text-sm text-black font-black">${moduleCost(selected, tierKey).toLocaleString()}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 mb-2">
              <MonoBadge>Difficulty: {diffLabel(catalogOf(selected.type)?.diff)}</MonoBadge>
              {(v.perModule[selected.id] || []).length === 0
                ? <MonoBadge tone="ok">Fit OK</MonoBadge>
                : (v.perModule[selected.id] || []).map((w, i) => <MonoBadge key={i} tone="warn">{w}</MonoBadge>)}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <NBtn variant="ghost" onClick={() => dupM(selected.id)} testid="van-module-dup"><Copy size={13} /> Duplicate</NBtn>
              <NBtn variant="ghost" onClick={() => removeM(selected.id)} testid="van-module-del"><Trash2 size={13} /> Remove</NBtn>
            </div>
          </NCard>
        ) : (
          <NCard className="p-3">
            <p className="font-mono text-[10px] text-black/60">Tap a module in the plan to edit its size, material and budget — or add a new one below.</p>
          </NCard>
        )}

        <NBtn variant="gold" onClick={() => setShowCatalog(true)} testid="van-add-module"><Plus size={15} /> Add Module</NBtn>
        <div className="h-2" />
      </div>

      {/* module catalog sheet */}
      {showCatalog && (
        <div className="absolute inset-0 z-50 bg-black/50 flex items-end" onClick={() => setShowCatalog(false)} data-testid="van-catalog">
          <div className="w-full max-w-[412px] mx-auto bg-[#FDFBF7] border-t-4 border-black rounded-t-2xl p-4 max-h-[70%] overflow-y-auto no-scrollbar" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <p className="font-serif text-xl text-black">Modules</p>
              <button onClick={() => setShowCatalog(false)} className="w-8 h-8 grid place-items-center border-2 border-black rounded-lg bg-white"><X size={15} /></button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {MODULE_TYPES.map((t) => {
                const c = MODULE_CATALOG[t];
                return (
                  <button key={t} onClick={() => addModule(t)} data-testid={`van-cat-${t}`}
                    className="text-left p-2.5 rounded-xl border-2 border-black bg-white active:translate-x-[2px] active:translate-y-[2px] shadow-[3px_3px_0px_#000] active:shadow-none">
                    <p className="font-serif text-sm text-black leading-tight">{c.label}</p>
                    <p className="font-mono text-[8px] opacity-60 mt-0.5">{c.w}×{c.h}mm</p>
                    <p className="font-mono text-[9px] font-black mt-1">${Math.round(c.base * tier.mult)}</p>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <div className="shrink-0 border-t-2 border-black p-3 bg-[#FDFBF7] grid grid-cols-3 gap-2">
        <NBtn variant="ghost" onClick={save} disabled={saving} testid="van-save"><Save size={13} /> {saving ? "…" : "Save"}</NBtn>
        <NBtn variant="ghost" onClick={() => setScreen("blueprint")} testid="van-go-blueprint"><FileText size={13} /> Blueprint</NBtn>
        <NBtn variant="gold" onClick={() => setScreen("3d")} testid="van-go-3d"><Box size={13} /> 3D</NBtn>
      </div>
    </div>
  );
}
